import React from 'react'
import { deliverableStage } from '../utils/deliverables'
import { normaliseTimecode } from '../utils/time'
import { saveDeliverableOrder } from '../utils/order'
import {
  PORTAL_UPLOAD_EDITOR,
  fetchDeliverableTranscript,
  insertTranscriptRevision,
} from '../utils/transcriptStore'
import { requestTranscriptJob } from '../utils/transcriptJobs'
import {
  publishVersionRow,
  setVersionVisibilityRow,
  updateCurrentVersionLinks,
} from '../utils/versionStore'
import { resolveChangeRequest as resolveRemoteChangeRequest } from '../utils/sharedReview'
import {
  deleteDeliverableRow,
  insertDeliverable,
  saveDeliverableOrderRows,
  updateDeliverableRow,
} from '../utils/projectWrites'
import { insertApproval, insertChangeRequest } from '../utils/sharedReview'
import type { StaffSession } from '../utils/staffAuth'
import type { ClientAlert, Deliverable, Project } from '../types/portal'
import { clock, stamp, uid, versionLabel } from './portalHelpers'
import type { ClientRoute, PortalState } from './portalTypes'

/**
 * The deliverable level of the Portal provider: opening, adding, editing,
 * reordering and publishing deliverables, client review actions, and the
 * transcript actions.
 *
 * Deliberately NOT a hook, despite the file name: `buildDeliverableActions`
 * is called inside the provider's existing `useMemo`, so the context value
 * keeps its single memo boundary and the same dependency array it always had.
 * The helpers it leans on (`log`, `patchDeliverable`, `appendNote`) are still
 * defined inside that memo and passed in.
 */

interface DeliverableActionInput {
  projectId: string
  activeProject: Project
  projects: Project[]
  activeDeliverableId: string
  clientPreview: boolean
  viewerName: string
  staffSession: StaffSession | null
  setActiveProjectId: React.Dispatch<React.SetStateAction<string>>
  setActiveDeliverableId: React.Dispatch<React.SetStateAction<string>>
  setClientRoute: React.Dispatch<React.SetStateAction<ClientRoute>>
  setClientAlerts: React.Dispatch<React.SetStateAction<ClientAlert[]>>
  mapDeliverables: (projectId: string, fn: (list: Deliverable[]) => Deliverable[]) => void
  patchDeliverable: (id: string, patch: Partial<Deliverable>) => void
  appendNote: (
    id: string,
    author: 'Client' | 'Portal' | 'System',
    body: string,
    authorName?: string,
    extra?: Partial<Deliverable>,
    timecode?: string,
  ) => void
  log: (actorName: string, action: string, deliverableId: string) => void
  notify: (message: string) => void
  reloadProjects: () => Promise<void>
}

export type DeliverableActions = Pick<
  PortalState,
  | 'toggleVersionVisibility'
  | 'clientDeliverables'
  | 'reviewDeliverables'
  | 'finalDeliverables'
  | 'activeDeliverable'
  | 'openDeliverable'
  | 'openShowDeliverable'
  | 'openShowFiles'
  | 'saveTranscript'
  | 'uploadTranscript'
  | 'restoreTranscriptRevision'
  | 'addDeliverable'
  | 'duplicateDeliverable'
  | 'updateDeliverable'
  | 'removeDeliverable'
  | 'setDeliverableOrder'
  | 'approveDeliverable'
  | 'requestChanges'
  | 'addNote'
  | 'publishVersion'
  | 'resolveChangeRequest'
>

export function buildDeliverableActions({
  projectId,
  activeProject,
  projects,
  activeDeliverableId,
  clientPreview,
  viewerName,
  staffSession,
  setActiveProjectId,
  setActiveDeliverableId,
  setClientRoute,
  setClientAlerts,
  mapDeliverables,
  patchDeliverable,
  appendNote,
  log,
  notify,
  reloadProjects,
}: DeliverableActionInput): DeliverableActions {
  return {
    toggleVersionVisibility: (deliverableId, versionId) => {
      const current = activeProject.deliverables
        .find((item) => item.id === deliverableId)
        ?.versions.find((entry) => entry.id === versionId)

      mapDeliverables(projectId, (list) =>
        list.map((item) =>
          item.id === deliverableId
            ? {
                ...item,
                versions: item.versions.map((entry) =>
                  entry.id === versionId
                    ? { ...entry, visibleToClient: !entry.visibleToClient }
                    : entry,
                ),
              }
            : item,
        ),
      )

      /* Stored, so a shared earlier version stays shared after a reload. */
      if (current) void setVersionVisibilityRow(versionId, !current.visibleToClient)
    },

    clientDeliverables: activeProject.deliverables.filter((item) => item.visibleToClient),
    reviewDeliverables: activeProject.deliverables.filter(
      (item) => item.visibleToClient && deliverableStage(item.status) === 'Review',
    ),
    finalDeliverables: activeProject.deliverables.filter(
      (item) => item.visibleToClient && deliverableStage(item.status) === 'Final Delivery',
    ),
    activeDeliverable: activeProject.deliverables.find((item) => item.id === activeDeliverableId),
    openDeliverable: (id) => {
      setActiveDeliverableId(id)
      const item = activeProject.deliverables.find((entry) => entry.id === id)
      setClientRoute(item?.type === 'Transcript' ? 'transcript' : 'episode')
    },
    /**
     * Same as `openDeliverable`, but for the Show Home headline banner's
     * action button — the ready-for-review deliverable it names can belong
     * to any episode of the show, not just whichever one happens to be
     * active, so the episode is switched to first.
     */
    openShowDeliverable: (projectId, deliverableId) => {
      setActiveProjectId(projectId)
      setActiveDeliverableId(deliverableId)
      const item = projects
        .find((entry) => entry.id === projectId)
        ?.deliverables.find((entry) => entry.id === deliverableId)
      setClientRoute(item?.type === 'Transcript' ? 'transcript' : 'episode')
    },
    /**
     * Same idea as `openShowDeliverable`, but for the Show Home headline's
     * "View Files" action on a completed show — there is no single
     * show-wide Files page, so this opens a specific episode's own one.
     */
    openShowFiles: (projectId) => {
      setActiveProjectId(projectId)
      setClientRoute('files')
    },

    /**
     * Saving never overwrites history it doesn't own: an earlier visit's
     * revision is always kept, so an earlier version can always be
     * restored. Repeat saves within the SAME visit (same `sessionId`,
     * same editor) fold into that visit's one revision instead, so a
     * reviewer editing back and forth doesn't stack a Version History row
     * per Save click — see `sessionId` on insertTranscriptRevision.
     */
    saveTranscript: async (id, text, doc, sessionId) => {
      /* Preview writes nothing, whatever it is asked to do. */
      if (clientPreview) return false
      const current = activeProject.deliverables.find((entry) => entry.id === id)
      if (!current) return false

      const editorName = viewerName

      /* The database decides whether this happened. Nothing local changes
         until it confirms, so a failed save can never look like a save. */
      /* Only the client's own new revision: Portal's upload was stored when
         it was uploaded, so there is no back-filling to do here. */
      const result = await insertTranscriptRevision({
        projectSlug: activeProject.slug,
        deliverableKey: id,
        text,
        editorName,
        doc,
        sessionId,
      })

      if (!result.ok) {
        notify(result.error ?? 'Transcript could not be saved.')
        return false
      }

      const history = current.transcriptRevisions?.length
        ? current.transcriptRevisions
        : current.transcript?.trim()
          ? [
              {
                id: uid('tr'),
                text: current.transcript,
                editorName: 'Portal',
                at: Date.now(),
              },
            ]
          : []

      /* The stored row id is adopted, so a reload never shows it twice. */
      const revisions = [
        ...history,
        { id: result.id ?? uid('tr'), text, doc, editorName, at: Date.now() },
      ]

      patchDeliverable(id, { transcript: text, transcriptRevisions: revisions })
      return true
    },

    /**
     * The upload is stored the moment it is parsed — the very first stored
     * revision for that deliverable, recorded as a Portal upload. Nothing
     * waits for a client edit, and nothing is reported as uploaded unless
     * the insert succeeded.
     */
    uploadTranscript: async (id, text, filename, words) => {
      const result = await insertTranscriptRevision({
        projectSlug: activeProject.slug,
        deliverableKey: id,
        text,
        editorName: PORTAL_UPLOAD_EDITOR,
      })

      if (!result.ok) {
        notify(`Transcript not saved · ${result.error}`)
        return { ok: false, error: result.error }
      }

      /**
       * Re-read immediately, so what the interface shows from here on is the
       * stored row rather than the text that was just parsed. If the read
       * comes back without it, the upload is reported as failed even though
       * the insert returned — the only proof that counts is a readable row.
       */
      const stored = await fetchDeliverableTranscript(activeProject.slug, id)

      if (stored.status === 'error' || !stored.transcript) {
        const detail =
          stored.status === 'error'
            ? stored.message
            : 'The transcript could not be read back after saving.'
        notify(`Transcript not saved · ${detail}`)
        return { ok: false, error: detail }
      }

      /* The source document is recorded on the deliverable so any staff
         member can see which file this came from. */
      patchDeliverable(id, {
        transcript: stored.transcript.text,
        transcriptFilename: filename,
        transcriptWordCount: words,
        transcriptRevisions: stored.transcript.revisions,
      })

      void updateDeliverableRow(projectId, activeProject.slug, id, {
        transcriptFilename: filename,
        transcriptWordCount: words,
      }).then((meta) => {
        if (!meta.ok) notify(`Transcript stored · file details not saved · ${meta.error}`)
      })

      notify(`Transcript saved · ${filename}`)
      return { ok: true }
    },

    restoreTranscriptRevision: async (id, revisionId) => {
      const current = activeProject.deliverables.find((entry) => entry.id === id)
      const target = current?.transcriptRevisions?.find((entry) => entry.id === revisionId)
      if (!current || !target) return false

      /* A restore is itself a saved state, stored like any other — the
         earlier states remain untouched. */
      const result = await insertTranscriptRevision({
        projectSlug: activeProject.slug,
        deliverableKey: id,
        text: target.text,
        editorName: staffSession?.name ?? 'Portal',
        /* Restoring a state restores its tracked deletions with it. */
        doc: target.doc,
      })

      if (!result.ok) {
        notify(result.error ?? 'Transcript could not be restored.')
        return false
      }

      const revisions = [
        ...(current.transcriptRevisions ?? []),
        {
          id: result.id ?? uid('tr'),
          text: target.text,
          doc: target.doc,
          editorName: staffSession?.name ?? 'Portal',
          at: Date.now(),
        },
      ]

      patchDeliverable(id, { transcript: target.text, transcriptRevisions: revisions })
      return true
    },

    addDeliverable: (input) => {
      const newId = uid('d')
      const order = activeProject.deliverables.length
      mapDeliverables(projectId, (list) => [
        ...list,
        {
          id: newId,
          name: input.name,
          type: input.type,
          quantity: '',
          status: input.status,
          version: input.version,
          versionDate: input.version ? stamp() : '—',
          runtime: '—',
          driveUrl: input.driveUrl,
          audioUrl: input.audioUrl,
          videoFormat: input.type === 'Video' ? input.videoFormat : undefined,
          internalNote: '',
          visibleToClient: input.visibleToClient,
          transcript: input.transcript,
          notes: [],
          changeRequests: [],
          versions: [],
        },
      ])

      void insertDeliverable(
        projectId,
        activeProject.slug,
        {
          id: newId,
          name: input.name,
          type: input.type,
          quantity: '',
          status: input.status,
          version: input.version,
          versionDate: '—',
          runtime: '—',
          driveUrl: input.driveUrl,
          audioUrl: input.audioUrl,
          internalNote: '',
          visibleToClient: input.visibleToClient,
          transcriptFilename: input.transcriptFilename,
          transcriptWordCount: input.transcriptWordCount,
          notes: [],
          changeRequests: [],
          versions: [],
        },
        order,
      ).then(async (result) => {
        if (!result.ok) {
          notify(`Not saved · ${result.error}`)
          /* The optimistic row never existed in the database — take it back
             off screen rather than leave it looking saved. */
          mapDeliverables(projectId, (list) => list.filter((item) => item.id !== newId))
          return
        }

        /* A transcript chosen while creating the deliverable is stored as
           soon as the row it belongs to exists. */
        if (input.transcript?.trim()) {
          const stored = await insertTranscriptRevision({
            projectSlug: activeProject.slug,
            deliverableKey: newId,
            text: input.transcript,
            editorName: PORTAL_UPLOAD_EDITOR,
          })
          if (!stored.ok) notify(`Transcript not saved · ${stored.error}`)
        }

        await reloadProjects()
      })
    },

    /**
     * Copies the useful setup — name, type, framing, visibility — but never
     * the client's feedback, approvals or version history. The copy is new
     * work with its own id, starting at the beginning again.
     */
    duplicateDeliverable: (id) => {
      const source = activeProject.deliverables.find((item) => item.id === id)
      if (!source) return
      const newId = uid('d')
      const order = activeProject.deliverables.length
      const copy: Deliverable = {
        ...source,
        id: newId,
        name: `${source.name} (Copy)`,
        status: 'Planned',
        version: '',
        versionDate: '—',
        runtime: '—',
        driveUrl: '',
        audioUrl: '',
        visibleToClient: false,
        transcript: undefined,
        transcriptRevisions: undefined,
        notes: [],
        changeRequests: [],
        versions: [],
        approvedBy: undefined,
        approvedAt: undefined,
      }

      mapDeliverables(projectId, (list) => [...list, copy])

      void insertDeliverable(projectId, activeProject.slug, copy, order).then(async (result) => {
        if (!result.ok) {
          notify(`Not saved · ${result.error}`)
          mapDeliverables(projectId, (list) => list.filter((item) => item.id !== newId))
          return
        }
        await reloadProjects()
      })
    },

    updateDeliverable: (id, patch) => {
      const before = activeProject.deliverables.find((item) => item.id === id)
      patchDeliverable(id, patch)
      void updateDeliverableRow(projectId, activeProject.slug, id, patch).then((result) => {
        if (result.ok) return
        /* The database refused the change — put the deliverable back exactly
           as it was, not just tell the person it failed. */
        if (before) patchDeliverable(id, before)
        notify(`Not saved · ${result.error}`)
      })

      /* Correcting a link without publishing changes the CURRENT version's
         link, so the stored current version row is kept in step. Archived
         versions are never touched by this. */
      if (patch.driveUrl !== undefined || patch.audioUrl !== undefined) {
        void updateCurrentVersionLinks(projectId, id, {
          driveUrl: patch.driveUrl,
          audioUrl: patch.audioUrl,
        })
      }

      /* A transcript is stored by uploadTranscript at the moment it is
         uploaded, so there is deliberately nothing to store here. */
    },

    removeDeliverable: (id) => {
      const previous = activeProject.deliverables
      mapDeliverables(projectId, (list) => list.filter((item) => item.id !== id))
      void deleteDeliverableRow(projectId, id).then((result) => {
        if (result.ok) return
        /* Still in the database — put it back on screen rather than leave a
           removal that silently didn't happen. */
        mapDeliverables(projectId, () => previous)
        notify(`Not removed · ${result.error}`)
      })
    },

    setDeliverableOrder: (ids) =>
      mapDeliverables(projectId, (list) => {
        const next = ids
          .map((id) => list.find((item) => item.id === id))
          .filter((item): item is Deliverable => Boolean(item))
        if (next.length !== list.length) return list

        const previousIds = list.map((item) => item.id)
        saveDeliverableOrder(projectId, ids)
        void saveDeliverableOrderRows(projectId, ids).then((result) => {
          if (result.ok) return
          /* The database refused the reorder — put the order back exactly as
             it was, in the local cache and on screen, and say so. */
          saveDeliverableOrder(projectId, previousIds)
          mapDeliverables(projectId, (current) =>
            previousIds
              .map((id) => current.find((item) => item.id === id))
              .filter((item): item is Deliverable => Boolean(item)),
          )
          notify(`Order not saved · ${result.error}`)
        })
        return next
      }),

    approveDeliverable: (id) => {
      if (clientPreview) return
      const current = activeProject.deliverables.find((item) => item.id === id)
      const who = viewerName
      /* Recorded so the studio can see who approved the current version. */
      mapDeliverables(projectId, (list) =>
        list.map((item) =>
          item.id === id ? { ...item, approvedBy: who, approvedAt: Date.now() } : item,
        ),
      )
      appendNote(
        id,
        'System',
        `${who} approved ${current?.version || 'this version'}`,
        undefined,
        { status: 'Approved' },
      )
      log(who, `approved ${current?.name ?? 'a deliverable'}`, id)

      /* Shared: the approval must be visible on every other device. */
      void insertApproval({
        projectSlug: activeProject.slug,
        deliverableKey: id,
        displayName: who,
        version: current?.version ?? '',
      }).then((ok) => {
        if (!ok) notify('Approved here, but not saved — other devices won’t see this yet.')
      })
    },

    /**
     * Ongoing: anyone on the client team can keep adding changes against the
     * current version. Only the first one raises a status event.
     */
    requestChanges: (id, body, timecode) => {
      if (clientPreview) return
      const who = viewerName
      const current = activeProject.deliverables.find((item) => item.id === id)
      const version = current?.version ?? ''
      const localId = uid('cr')
      const code = normaliseTimecode(timecode ?? '')
      const firstForVersion = !current?.changeRequests.some(
        (request) => request.version === version && !request.resolved,
      )

      mapDeliverables(projectId, (list) =>
        list.map((item) =>
          item.id === id
            ? {
                ...item,
                status: 'Changes Requested',
                notes: firstForVersion
                  ? [
                      ...item.notes,
                      {
                        id: uid('n'),
                        author: 'System',
                        time: clock(),
                        body: `Changes requested on ${item.version || 'this version'}`,
                      },
                    ]
                  : item.notes,
                changeRequests: [
                  ...item.changeRequests,
                  {
                    id: localId,
                    body,
                    timecode: code,
                    authorName: who,
                    time: clock(),
                    submitted: `${stamp()} · ${clock()}`,
                    at: Date.now(),
                    version,
                    resolved: false,
                  },
                ],
              }
            : item,
        ),
      )

      if (firstForVersion) {
        log(who, `requested changes to ${current?.name ?? 'a deliverable'}`, id)
      }

      /* Shared: stored once, seen by everyone holding the project link. The
         local row adopts the stored id so it is never duplicated on reload. */
      void insertChangeRequest({
        projectSlug: activeProject.slug,
        deliverableKey: id,
        displayName: who,
        comment: body,
        timecode: code,
        version,
      }).then((remoteId) => {
        if (!remoteId) return
        mapDeliverables(projectId, (list) =>
          list.map((item) =>
            item.id === id
              ? {
                  ...item,
                  changeRequests: item.changeRequests.map((request) =>
                    request.id === localId
                      ? { ...request, id: remoteId, shared: true }
                      : request,
                  ),
                }
              : item,
          ),
        )
      })
    },

    addNote: (id, author, body, timecode) => {
      if (clientPreview) return
      const who = author === 'Client' ? viewerName : 'Portal'
      appendNote(id, author, body, who, {}, normaliseTimecode(timecode ?? ''))
      if (author === 'Client') {
        const current = activeProject.deliverables.find((item) => item.id === id)
        log(who, `commented on ${current?.name ?? 'a deliverable'}`, id)
      }
    },

    publishVersion: async (id, version, links) => {
      const current = activeProject.deliverables.find((item) => item.id === id)
      const next = version.trim() || current?.version || 'V1'

      /**
       * The version loaded for this deliverable BEFORE publishing began. It
       * is read from the loaded project, never from the edited form, so a
       * stale publish is detected by the database. '' means "no version yet",
       * which is how V1 is published.
       */
      const expected = current?.version?.trim() ?? ''

      /**
       * The transition is performed by the database in one transaction. It
       * archives the version and links currently STORED against the
       * deliverable — never the new link staff have just typed — then
       * records the new version as current with its publication time.
       *
       * Nothing local changes until it succeeds, so a refused publish leaves
       * the interface showing the truth.
       */
      const result = await publishVersionRow({
        projectId,
        projectSlug: activeProject.slug,
        deliverableKey: id,
        version: next,
        expectedCurrentVersion: expected,
        driveUrl: links?.driveUrl ?? current?.driveUrl ?? '',
        audioUrl: links?.audioUrl ?? current?.audioUrl ?? '',
      })

      if (!result.ok) {
        notify(`Not published · ${result.error}`)
        return false
      }

      mapDeliverables(projectId, (list) =>
        list.map((item) => {
          if (item.id !== id) return item
          const superseded =
            item.version && item.version !== next
              ? [
                  {
                    id: uid('v'),
                    version: item.version,
                    label: versionLabel(item.status),
                    date: item.versionDate,
                    /* The link this version was actually reviewed on. */
                    driveUrl: item.driveUrl,
                    changeCount: item.changeRequests.filter(
                      (request) => request.version === item.version,
                    ).length,
                    visibleToClient: false,
                  },
                  ...item.versions,
                ]
              : item.versions
          return {
            ...item,
            version: next,
            versionDate: stamp(),
            status: 'Ready for Review',
            /* The new cut's links belong to the new version only — the
               superseded entry above already holds the previous ones. */
            driveUrl: links?.driveUrl ?? item.driveUrl,
            audioUrl: links?.audioUrl ?? item.audioUrl,
            versions: superseded,
            /* A new cut is unapproved until the client says otherwise. */
            approvedBy: undefined,
            approvedAt: undefined,
            changeRequests: item.changeRequests.map((request) => ({ ...request, resolved: true })),
            notes: [
              ...item.notes,
              { id: uid('n'), author: 'System', time: clock(), body: `Portal published ${next}` },
            ],
          }
        }),
      )
      /**
       * Only claim feedback has been addressed when the client actually asked
       * for changes on the version being replaced.
       */
      const hadFeedback = Boolean(
        current &&
          (current.status === 'Changes Requested' ||
            current.changeRequests.some((request) => !request.resolved)),
      )
      const firstCut = !current || current.versions.length === 0
      setClientAlerts((list) => [
        ...list.filter((alert) => alert.deliverableId !== id),
        {
          id: uid('alert'),
          projectId,
          kind: 'review',
          title: `${(current?.name ?? 'New cut').toUpperCase()} — ${next} is ready`,
          body: hadFeedback
            ? 'Your requested changes have been addressed.'
            : firstCut
              ? 'Your first cut is ready for review.'
              : 'A new version is ready for review.',
          actionLabel: `Review ${next}`,
          deliverableId: id,
        },
      ])
      log('Portal', `published ${next} of ${current?.name ?? 'a deliverable'}`, id)
      notify(`${next} published. The client can now review this version.`)

      /* Read the stored transition back, so the current version, links,
         status, publication date and Version History all come from the
         database rather than from this browser. */
      await reloadProjects()

      /**
       * A new cut needs a fresh transcript — the one on file (if any)
       * described the previous edit and would silently go stale
       * otherwise. Queued the same way the "Generate Transcript" button
       * does, via the transcript_jobs table for the unattended Descript
       * worker to pick up — never called directly from here. Video only:
       * audio transcript generation stays a manual click (10 Sep 2026
       * review-flow decisions, item 4), and this is deliberately
       * best-effort — a queueing failure must never undo or block a
       * publish that has already succeeded.
       */
      if (current?.type === 'Video') {
        const freshUrl = links?.driveUrl ?? current?.driveUrl ?? ''
        if (freshUrl.trim()) {
          void requestTranscriptJob({
            projectId,
            deliverableKey: id,
            sourceName: current.name,
            sourceKind: 'video',
            sourceUrl: freshUrl,
            requestedBy: staffSession?.name ?? 'Portal',
          })
        }
      }

      return true
    },

    /* Staff-only. The stored row is updated, never deleted, so the completed
       request stays on the record for both sides. */
    resolveChangeRequest: (id, requestId) => {
      const completedAt = new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
      const completedBy = staffSession?.name.split(' ')[0] ?? 'Portal'

      mapDeliverables(projectId, (list) =>
        list.map((item) =>
          item.id === id
            ? {
                ...item,
                changeRequests: item.changeRequests.map((request) =>
                  request.id === requestId
                    ? { ...request, resolved: true, completedAt, completedBy }
                    : request,
                ),
              }
            : item,
        ),
      )

      void resolveRemoteChangeRequest(requestId)
    },
  }
}
